import React, {useState, useMemo, useRef, useEffect, useContext} from 'react'
import _ from 'lodash'
import EventBox from './EventBox'
import moment from 'moment'

import MoreEventBox from './MoreEventBox'
import {CreateEventGroupings} from './util'
import NewEventBox from './NewEventBox'
import {snap} from '../../utils/ApMath'
import {ReservationContext} from '../../context/Reservation'
import useViewColor from './useViewColor'
import {fill} from '../../utils/ApColor'

const DayColumn = ({events, date = moment(), onClickEvent = () => {}, onClickMoreSidebar}) => {
  // move this to as event instead
  const {update, setPopReserve} = useContext(ReservationContext)
  const refContainer = useRef(null)
  const refNewEventBox = useRef(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const color = useViewColor()

  const CTimeSlots = Array.from({length: 24}).map((e, i) => {
    return <div key={'ts_' + i} className="time-slot"></div>
  })

  const CEventBoxes = useMemo(() => {
    if (containerWidth === 0) return []

    const maxColumns = Math.floor(containerWidth / 55)
    let groupings = CreateEventGroupings(events, maxColumns)

    return groupings.reduce((ret, gc) => {
      // create components from columns
      let colCount = gc.columns.length
      let width = !!gc.more ? containerWidth - 64 : containerWidth
      let colWidth = 1 / colCount //percent
      let components = gc.columns.reduce((colRet, col, i) => {
        let left = colWidth * i
        let cs = col.map((ev, j) => (
          <EventBox
            key={j}
            data={{...ev, left: left * width, right: colWidth * width}}
            onClick={onClickEvent}
          />
        ))
        return [...colRet, ...cs]
      }, [])
      if (gc.more) {
        components.push(
          <MoreEventBox
            key={'more_event'}
            data={{...gc.more, left: width, right: 64}}
            onClick={onClickMoreSidebar}
          />
        )
      }

      return [...ret, ...components]
    }, [])
  }, [containerWidth, onClickEvent, onClickMoreSidebar, events])

  useEffect(() => {
    const f = () => {
      const rect = refContainer.current.getBoundingClientRect()
      setContainerWidth(rect.width)
    }

    f() // run on mount

    // debounce function
    const df = _.debounce(f, 500)
    window.addEventListener('resize', df)
    return () => {
      window.removeEventListener('resize', df)
    }
  }, [])

  // drag n drop reserve
  useEffect(() => {
    const topHandle = refNewEventBox.current
    const ref = refContainer.current
    const refScroller = ref.closest('.main')

    let reserveOnOrAfter = moment()

    let initClientY = 0
    let initScrollY = 0
    let offsetY = 0
    let scrollOffsetY = 0
    let startY = 0
    let mode = null // range | top | bottom
    let maxIndex = 48

    let start = null
    let end = null

    let isDrag = false

    const handleRangeAdjustment = height => {
      const curDate = date.clone().startOf('day')
      // snap to 30 mins
      const snapStartY = snap(startY, 32, Math.floor)
      const snapHeight = snap(height, 32, Math.ceil)

      const indexStartY = Math.min(maxIndex - 1, snapStartY / 32)
      const indexHeight = Math.max(
        Math.min(maxIndex, snapHeight / 32 + indexStartY),
        indexStartY + 1
      )

      start = curDate.clone().add(indexStartY * 30, 'minutes')
      end = curDate.clone().add(indexHeight * 30, 'minutes')

      if (start >= reserveOnOrAfter.valueOf()) {
        // calculate time based on index
        isDrag = true
        topHandle.style.display = 'flex' // force to use css class
        topHandle.textContent = `${start.format('h:mm a')} - ${end.format('h:mm a')} `
        topHandle.style.top = indexStartY * 32 + 'px'
        topHandle.style.height = (indexHeight - indexStartY) * 32 + 'px'
      }
    }

    const handleMouseMove = e => {
      if (!!mode) {
        offsetY = e.clientY - initClientY
        handleRangeAdjustment(offsetY + scrollOffsetY)
      }
    }

    const handleScroll = e => {
      if (!!mode) {
        scrollOffsetY = refScroller.scrollTop - initScrollY
        handleRangeAdjustment(offsetY + scrollOffsetY)
      }
    }

    const handleMouseDown = e => {
      if (e?.button !== 0) return
      if (e.target.getAttribute('data-x') !== 'date-selection') return

      reserveOnOrAfter = moment().set({
        minute: snap(moment().minutes() + 1, 30, Math.ceil), // +1 so when reserving at exact 30 mins 0, it'll go to next 30 mins
        second: 0,
        millisecond: 0,
      })
      // get current minute and round to ceil 30 mins
      // let minuteReserveOnOrAfter = reserveOnOrAfter,minute

      let rect = ref.getBoundingClientRect()
      initClientY = e.clientY
      initScrollY = refScroller.scrollTop
      scrollOffsetY = 0
      offsetY = 0
      startY = e.clientY - rect.top
      mode = 'range'
      topHandle.style.top = startY + 'px'
      topHandle.style.height = 0 + 'px'
    }

    const handleMouseUp = e => {
      mode = null
      if (!isDrag) return
      isDrag = false
      topHandle.style.display = 'none' // force to use css class
      update('date_from', start)
      update('date_to', end)
      setPopReserve(true)
    }
    // register events
    refScroller.addEventListener('scroll', handleScroll)
    window.addEventListener('mousemove', handleMouseMove)
    ref.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      refScroller.removeEventListener('scroll', handleScroll)
      window.removeEventListener('mousemove', handleMouseMove)
      ref.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [date, update, setPopReserve])

  return (
    <div className="day-column padded-column">
      <div className="time-slot time-slot--filler"></div>
      {CTimeSlots}
      <div className="events-container" data-x="date-selection" ref={refContainer}>
        {CEventBoxes}
        <NewEventBox
          ref={refNewEventBox}
          style={{
            left: 0,
            right: 0,
            marginRight: 0,
            display: 'none',
            backgroundColor: fill(color, 0.35),
            borderColor: color,
          }}
        />
      </div>
    </div>
  )
}

export default DayColumn
